import { useCallback, useEffect, useState } from 'react';
import { Currency, ResponseError, Response } from '@ui/src';
import { Balance } from '../../types';
import {
  useChangeBalanceStatusMutation,
  useLazyGetCustomerAccountBalanceQuery,
} from '../../../customers/store/services/customers';

const useCurrencies = ({ selectedAccount }: { selectedAccount?: string }) => {
  const [balances, setBalances] = useState<Balance[]>([]);
  const [blockPopupVisible, setBlockPopupVisible] = useState(false);
  const [enablePopupVisible, setEnablePopupVisible] = useState(false);
  const [selectedBlockCurrency, setSelectedBlockCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [selectedEnableCurrency, setSelectedEnableCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [error, setError] = useState('');

  const [getBalance, { isLoading, isFetching }] =
    useLazyGetCustomerAccountBalanceQuery();
  const [onEnableBalance, { isLoading: enableInProgress }] =
    useChangeBalanceStatusMutation();
  const [onDisableBalance, { isLoading: disableInProgress }] =
    useChangeBalanceStatusMutation();

  const onEnableBalanceAction = async () => {
    const response = (await onEnableBalance({
      id: selectedAccount as string,
      body: {
        currencyId: selectedEnableCurrency?.id as string,
        active: true,
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: Balance };
    };

    if (response?.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setError(response?.error?.data?.message[0]?.value as string);
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setError(response?.error?.data?.message);
      } else {
        setError('Internal Server Error. Try Again');
      }
    } else if (response?.data && response?.data?.data) {
      const nextBalances = ([] as Balance[]).concat(balances);
      nextBalances.push(response.data.data);

      setBalances(nextBalances);
      setSelectedEnableCurrency(undefined);
      setEnablePopupVisible(false);
    }
  };

  const onDisableBalanceAction = async () => {
    const response = (await onDisableBalance({
      id: selectedAccount as string,
      body: {
        currencyId: selectedBlockCurrency?.id as string,
        active: false,
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: Balance };
    };

    if (response?.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setError(response?.error?.data?.message[0]?.value as string);
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setError(response?.error?.data?.message);
      } else {
        setError('Internal Server Error. Try Again');
      }
    } else if (response?.data && response?.data?.data) {
      setBalances(
        balances?.filter((item) => item.id !== response?.data?.data?.id),
      );
      setSelectedBlockCurrency(undefined);
      setBlockPopupVisible(false);
    }
  };

  const getBalancesAction = useCallback(async (id: string) => {
    const response = (await getBalance(id)) as { data: Response<Balance> };

    if (response && response?.data?.data) {
      setBalances(response?.data?.data?.edges?.map((item) => item.node));
    }
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      getBalancesAction(selectedAccount);
    }
  }, [selectedAccount]);

  return {
    balances,
    setBlockPopupVisible,
    setSelectedBlockCurrency,
    setEnablePopupVisible,
    setSelectedEnableCurrency,
    isLoading,
    isFetching,
    selectedBlockCurrency,
    setError,
    blockPopupVisible,
    error,
    disableInProgress,
    onDisableBalanceAction,
    selectedEnableCurrency,
    enablePopupVisible,
    enableInProgress,
    onEnableBalanceAction,
  };
};

export default useCurrencies;
